import { CatalogLibrary, CatalogLibraryEntry } from '@kaoto/camel-catalog/types';
import { isDefined } from '@kaoto/forms';
import { Content, ContentVariants } from '@patternfly/react-core';
import { createContext, FunctionComponent, PropsWithChildren, useEffect, useMemo, useState } from 'react';

import { LoadDefaultCatalog } from '../components/LoadDefaultCatalog';
import { Loading } from '../components/Loading';
import { fetchXsltXPathFunctions } from '../dynamic-catalog/support/fetch-xslt-xpath-functions';
import { useKaotoResourceContext } from '../hooks/useKaotoResourceContext/useKaotoResourceContext';
import { LoadingStatus } from '../models';
import { SourceSchemaType } from '../models/camel/source-schema-type';
import { XPathFunctionCatalogService } from '../services/xpath/catalog/xpath-function-catalog.service';
import { findCatalog } from '../utils/catalog-helper';

export interface IRuntimeContext {
  basePath: string;
  catalogLibrary: CatalogLibrary | undefined;
  selectedCatalog: CatalogLibraryEntry | undefined;
  setSelectedCatalog: (catalog: CatalogLibraryEntry | undefined) => void;
}

export const RuntimeContext = createContext<IRuntimeContext | undefined>(undefined);

interface IRuntimeProvider {
  catalogUrl: string;
  runtimeCatalogName: string;
  testingCatalogName: string;
}

/**
 * Loader for the available Catalog library.
 */
export const RuntimeProvider: FunctionComponent<PropsWithChildren<IRuntimeProvider>> = ({
  catalogUrl,
  runtimeCatalogName,
  testingCatalogName,
  children,
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [catalogLibrary, setCatalogLibrary] = useState<CatalogLibrary | undefined>(undefined);
  const [selectedCatalog, setSelectedCatalog] = useState<CatalogLibraryEntry | undefined>();

  const { kaotoResource } = useKaotoResourceContext();
  const currentSchemaType = kaotoResource.getType();
  const catalogName = currentSchemaType === SourceSchemaType.Test ? testingCatalogName : runtimeCatalogName;

  const basePath = catalogUrl.substring(0, catalogUrl.lastIndexOf('/'));
  const request = useMemo(
    () => ({ basePath, catalogName, catalogUrl, currentSchemaType }),
    [basePath, catalogName, catalogUrl, currentSchemaType],
  );
  const [loadState, setLoadState] = useState({ request, status: LoadingStatus.Loading });
  // A new request hides interactive children immediately, before its effect starts fetching.
  const loadingStatus = loadState.request === request ? loadState.status : LoadingStatus.Loading;

  useEffect(() => {
    const controller = new AbortController();
    fetch(catalogUrl)
      .then((response) => response.json())
      .then((catalogLibrary: CatalogLibrary) => {
        if (controller.signal.aborted) return;
        let catalogLibraryEntry: CatalogLibraryEntry | undefined = undefined;
        if (isDefined(catalogName)) {
          catalogLibraryEntry = catalogLibrary.definitions.find((c: CatalogLibraryEntry) => c.name === catalogName);
        }
        if (!isDefined(catalogLibraryEntry)) {
          catalogLibraryEntry = findCatalog(currentSchemaType, catalogLibrary);
        }

        setCatalogLibrary(catalogLibrary);
        setSelectedCatalog(catalogLibraryEntry);

        return fetchXsltXPathFunctions(basePath, catalogLibrary, controller.signal).catch(() => {
          /* XSLT catalog load failure is non-fatal — XPath editor falls back to hardcoded functions */
        });
      })
      .then(() => {
        if (!controller.signal.aborted) setLoadState({ request, status: LoadingStatus.Loaded });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setErrorMessage(error.message);
        setLoadState({ request, status: LoadingStatus.Error });
      });

    return () => {
      controller.abort();
      XPathFunctionCatalogService.clear();
    };
  }, [basePath, catalogName, catalogUrl, currentSchemaType, request]);

  const runtimeContext: IRuntimeContext = useMemo(
    () => ({
      basePath,
      catalogLibrary,
      selectedCatalog,
      setSelectedCatalog,
    }),
    [basePath, catalogLibrary, selectedCatalog],
  );

  return (
    <RuntimeContext.Provider value={runtimeContext}>
      {loadingStatus === LoadingStatus.Loading && (
        <Loading>
          <Content data-testid="loading-library" component={ContentVariants.h3}>
            Loading Library...
          </Content>
        </Loading>
      )}

      {loadingStatus === LoadingStatus.Error && (
        <LoadDefaultCatalog errorMessage={errorMessage}>
          Some catalog library files might not be available.
          <br />
          Please try to reload the page or load the default Catalog.
        </LoadDefaultCatalog>
      )}

      {loadingStatus === LoadingStatus.Loaded && children}
    </RuntimeContext.Provider>
  );
};
